import { action, makeObservable, observable, toJS } from 'mobx'

import type IntegrationStore from '@src/service/integration-store'
import type { Model } from '@src/service/model/base'

import type { Integration } from '..'

import type CallDeposition from './CallDeposition'
import type ContactImport from './ContactImport'
import type CreatedBy from './CreatedBy'
import type LoggableActivity from './LoggableActivity'

// Combined interface for all integration properties
export interface BaseIntegration {
  id: string
  type: string
  userName: string
  refreshToken: string | null
  accessToken: string | null
  accessTokenExpiresAt: Date | null
  resourceIds: string[]
  logCalls: LoggableActivity
  logMessages: LoggableActivity
  contactProperties: string[]
  callDepositions: CallDeposition[]
  contactImport: ContactImport | null
  createdBy: CreatedBy | null
}

// Unified base class for all integration models
export abstract class BaseIntegrationModel<T extends BaseIntegration>
  implements Model, BaseIntegration
{
  readonly raw: T

  constructor(
    protected integrationStore: IntegrationStore,
    initialValues: T,
    attrs: Partial<T> = {},
  ) {
    this.raw = { ...initialValues }
    this.deserialize(attrs)

    makeObservable<this, any>(this, {
      raw: observable.deep,
      update: action,
      delete: action,
      deserialize: action,
      serialize: action,
    })
  }

  deserialize(json: Partial<T>): this {
    Object.assign(this.raw, json)
    return this
  }

  serialize(): T {
    return toJS(this.raw)
  }

  get id(): string {
    return this.raw.id
  }

  abstract get type(): string

  get userName(): string {
    return this.raw.userName
  }

  get resourceIds(): string[] {
    return this.raw.resourceIds
  }

  get refreshToken(): string | null {
    return this.raw.refreshToken
  }

  get accessToken(): string | null {
    return this.raw.accessToken
  }

  get logCalls(): LoggableActivity {
    return this.raw.logCalls
  }

  get logMessages(): LoggableActivity {
    return this.raw.logMessages
  }

  get accessTokenExpiresAt(): Date | null {
    return this.raw.accessTokenExpiresAt
  }

  get contactProperties(): string[] {
    return this.raw.contactProperties
  }

  get callDepositions(): CallDeposition[] {
    return this.raw.callDepositions
  }

  get contactImport(): ContactImport | null {
    return this.raw.contactImport
  }

  get createdBy(): CreatedBy | null {
    return this.raw.createdBy
  }

  update(attrs: Partial<T>) {
    Object.assign(this.raw, attrs)
    return this.integrationStore.update(this as unknown as Integration)
  }

  delete() {
    return this.integrationStore.delete(this as unknown as Integration)
  }
}
